import React, { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FilterSection } from "../../../components/patients/FilterSection";
import { PatientListTable } from "../../../components/patients/PatientListTable";
import { FilterModal } from "../../../components/patients/FilterModal";
import { useToast } from "@/hooks/use-toast";
import { usePatientGroups } from "@/hooks/activity/usePatientGroups";
import { useAuth } from "@/contexts/AuthContext";
import LoaderComponent from "@/components/common/LoaderComponent";
import axios from "axios";
import { API_CONFIG } from "@/config/envConfig";

interface PatientDetail {
  enrollment_patient_id: number;
  first_name: string;
  middle_name?: string;
  last_name: string;
  emr_id: string;
  gender?: string;
  dob?: string;
  mobile_number?: string;
  home_number?: string;
  provider_name?: string;
  clinic_name?: string;
}

export default function AddPatients() {
  const { patientGroupId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { hasuraToken, user, token } = useAuth();
  const { patientGroups, refetch: refetchGroups } = usePatientGroups();

  // Filter states
  const [nameValue, setNameValue] = useState("");
  const [phoneValue, setPhoneValue] = useState("");
  const [emrValue, setEmrValue] = useState("");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<any>({});

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPatients, setSelectedPatients] = useState<string[]>([]);

  // Patient data states
  const [patients, setPatients] = useState<PatientDetail[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const itemsPerPage = 10;

  // Get current group data
  const currentGroup = useMemo(() => {
    return patientGroups.find(g => g.patient_group_id.toString() === patientGroupId);
  }, [patientGroups, patientGroupId]);

  // Fetch patients NOT in the current group
  const fetchPatients = async (searchFilters: any = {}) => {
    if (!hasuraToken || !patientGroupId) return;

    setIsLoading(true);
    try {
      const baseWhere: any = {
        patient_group_id: { _is_null: true }
      };

      const where = { ...baseWhere, ...searchFilters };

      const query = `
        query GetPatientDetailsForGroup($offset: Int, $limit: Int, $where: pep_vw_patient_details_bool_exp) {
          pep_vw_patient_details(offset: $offset, limit: $limit, where: $where) {
            enrollment_patient_id
            emr_patient_clinic_data_id
            emr_patient_master_id
            first_name
            last_name
            middle_name
            dob
            gender
            mobile_number
            home_number
            work_phone
            email
            address_line_one
            address_line_two
            city
            state
            zip_code
            emr_id
            emr_name
            clinic_id
            clinic_name
            clinic_facility_id
            clinic_facility_name
            provider_id
            provider_name
            patient_group_id
            created_by
            created_datetime
            updated_by
            updated_datetime
            identifier
            is_active
          }
          
          pep_vw_patient_details_aggregate(where: $where) {
            aggregate {
              count
            }
          }
        }
      `;

      const response = await axios.post(
        API_CONFIG.GRAPHQL_ENDPOINT,
        {
          query,
          variables: {
            offset: (currentPage - 1) * itemsPerPage,
            limit: itemsPerPage,
            where
          }
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${hasuraToken}`
          }
        }
      );

      if (response.data.data) {
        setPatients(response.data.data.pep_vw_patient_details || []);
        setTotalCount(response.data.data.pep_vw_patient_details_aggregate.aggregate.count || 0);
      }
    } catch (error) {
      console.error('Error fetching patients:', error);
      toast({
        title: "Error",
        description: "Failed to load available patients.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPatients();
  }, [hasuraToken, currentPage, patientGroupId]);

  // Transform patients data for display
  const displayPatients = useMemo(() => {
    return patients.map(patient => ({
      id: patient.enrollment_patient_id?.toString() || '',
      patientName: `${patient.first_name || ''} ${patient.middle_name || ''} ${patient.last_name || ''}`.trim(),
      emr: patient.emr_id || 'N/A',
      gender: patient.gender || 'N/A',
      dateOfBirth: patient.dob || 'N/A',
      age: patient.dob ? Math.floor((new Date().getTime() - new Date(patient.dob).getTime()) / 31557600000) : 0,
      phoneNumber: patient.mobile_number || patient.home_number || 'N/A',
      providerDetails: patient.provider_name || 'N/A',
      clinicDetails: patient.clinic_name || 'N/A',
      primaryDx: 'N/A',
      secondaryDx: 'N/A',
      insuranceType: 'N/A',
    }));
  }, [patients]);

  // Pagination logic
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const handleSearch = (filtersOverride?: any) => {
    setCurrentPage(1);
    const activeFilters = filtersOverride !== undefined ? filtersOverride : appliedFilters;
    const conditions: any[] = [];
    
    if (nameValue) {
      conditions.push({
        _or: [
          { first_name: { _ilike: `%${nameValue}%` } },
          { last_name: { _ilike: `%${nameValue}%` } }
        ]
      });
    }
    if (phoneValue) {
      conditions.push({ mobile_number: { _ilike: `%${phoneValue}%` } });
    }
    if (emrValue) {
      conditions.push({ emr_id: { _ilike: `%${emrValue}%` } });
    }

    // Add advanced filters
    if (activeFilters.clinic_id?.length > 0) {
      conditions.push({ clinic_id: { _in: activeFilters.clinic_id } });
    }
    if (activeFilters.clinic_facility_id?.length > 0) {
      conditions.push({ clinic_facility_id: { _in: activeFilters.clinic_facility_id } });
    }
    if (activeFilters.provider_id?.length > 0) {
      conditions.push({ provider_id: { _in: activeFilters.provider_id } });
    }
    if (activeFilters.primary_insurance_payor?.length > 0) {
      conditions.push({ primary_insurance_payor: { _in: activeFilters.primary_insurance_payor } });
    }
    if (activeFilters.secondary_insurance_payor?.length > 0) {
      conditions.push({ secondary_insurance_payor: { _in: activeFilters.secondary_insurance_payor } });
    }
    if (activeFilters.condition?.length > 0) {
      conditions.push({ condition: { _in: activeFilters.condition } });
    }
    if (activeFilters.zip_code?.length > 0) {
      conditions.push({ zip_code: { _in: activeFilters.zip_code } });
    }
    if (activeFilters.program_eligibility?.length > 0) {
      conditions.push({ program_eligibility: { _in: activeFilters.program_eligibility } });
    }

    const searchFilters = conditions.length > 0 ? { _and: conditions } : {};
    fetchPatients(searchFilters);
  };

  const handleFilter = () => {
    setIsFilterModalOpen(true);
  };

  const handlePatientSelect = (patientId: string) => {
    setSelectedPatients(prev => 
      prev.includes(patientId)
        ? prev.filter(id => id !== patientId)
        : [...prev, patientId]
    );
  };

  const handleSelectAll = () => {
    const allCurrentPageIds = displayPatients.map(p => p.id);
    const allSelected = allCurrentPageIds.every(id => selectedPatients.includes(id));
    
    if (allSelected) {
      setSelectedPatients(prev => prev.filter(id => !allCurrentPageIds.includes(id)));
    } else {
      setSelectedPatients(prev => [...new Set([...prev, ...allCurrentPageIds])]);
    }
  };


  const handleAddPatients = async () => {
    if (selectedPatients.length === 0) {
      toast({
        title: "No patients selected",
        description: "Please select patients to add to the group.",
        variant: "destructive",
      });
      return;
    }

    if (!currentGroup || !user || !token) {
      toast({
        title: "Error",
        description: "Missing required information to add patients.",
        variant: "destructive",
      });
      return;
    }

    setIsAdding(true);

    try {
      const patientIdsToAdd = selectedPatients.map(id => parseInt(id));
      
      const payload = {
        isArchived: false,
        patientIdsToAdd: patientIdsToAdd,
        patientIdsToRemove: []
      };

      const url = `${API_CONFIG.API_BASE}/api/ngpep/${user.id}/patient-group/${patientGroupId}`;
      
      await axios.put(url, payload, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      toast({
        title: "Patients added",
        description: `Successfully added ${selectedPatients.length} patient(s) to the group.`,
      });

      // Refetch groups and navigate back to edit page
      await refetchGroups();
      navigate(`/admin/patient-groups/edit/${patientGroupId}`);
    } catch (error) {
      console.error('Error adding patients:', error);
      toast({
        title: "Error",
        description: "Failed to add patients to the group.",
        variant: "destructive",
      });
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/30">
      <header className="bg-background border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/admin/patient-groups/edit/${patientGroupId}`)}
              className="p-2"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-2xl font-semibold text-foreground">Add Patients</h1>
          </div>
          <Button 
            onClick={handleAddPatients}
            disabled={selectedPatients.length === 0 || isAdding}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            {isAdding ? 'Adding...' : `Add Selected (${selectedPatients.length})`}
          </Button>
        </div>
      </header>

      <main className="p-6 space-y-6">
        {/* Filter Section */}
        <FilterSection
          nameValue={nameValue}
          phoneValue={phoneValue}
          emrValue={emrValue}
          onNameChange={setNameValue}
          onPhoneChange={setPhoneValue}
          onEmrChange={setEmrValue}
          onSearch={handleSearch}
          onFilter={handleFilter}
        />

        {/* Patients Section */}
        <div className="bg-background border rounded-lg p-6">
          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <LoaderComponent size="md" />
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Select patients to add to group
                </h3>
                <p className="text-sm text-muted-foreground">
                  Choose from available patients that are not currently in this group. Selected patients will be added to the group.
                </p>
              </div>

              <PatientListTable
            patients={displayPatients}
            selectedPatients={selectedPatients}
            onPatientSelect={handlePatientSelect}
            onSelectAll={handleSelectAll}
            filteredCount={totalCount}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
              />
            </>
          )}
        </div>
      </main>

      <FilterModal
        open={isFilterModalOpen}
        onOpenChange={setIsFilterModalOpen}
        initialFilters={appliedFilters}
        onApply={(filters) => {
          setAppliedFilters(filters);
          setIsFilterModalOpen(false);
          handleSearch(filters);
        }}
      />
    </div>
  );
}